/**
 * Entities the in-app assistant may query (request 0293 / platform spec 0012 §8.0 G5).
 *
 * One row per module page that exposes a queryable entity (`entity` on
 * `PaginatedCrudPage` / `CrudGrid`). Gated at runtime by the user's module
 * instances (same source as the sidebar / `app.openModule`): curation says what
 * *may* be exposed; module access says what *this* user sees.
 *
 * `label` / `singularLabel` come from the EntityDescriptor via registry lookup
 * (`getDescriptorForRule`) at resolve time — do not import descriptors here
 * (would inflate the entry chunk that reaches AppLayout). `pluralLabel` and
 * `moduleSlug` stay manual (pt-BR plurals are irregular; there is no automatic
 * link between module directory slug and entity name).
 */
import { getDescriptorForRule } from '@syscolect/domain'

/** Manual catalog row — labels resolved from the descriptor at use time. */
export interface AssistantQueryableEntitySpec {
  /** Registry / descriptor name (`Product`, `SalesOrder`, …). */
  entity: string
  /** Plural noun for compact result messages (`12 pedidos`). */
  pluralLabel: string
  /**
   * Module slug as in `/m/:slug` — directory name with dashes removed
   * (`sales-order` → `salesorder`).
   */
  moduleSlug: string
  /**
   * Opt-in for `data.createRecord` (request 0295). Flat catalogs only — no
   * child collections, no stock side-effects. Service is loaded dynamically
   * via `assistantCreateServices.ts` (never imported here — entry chunk).
   */
  creatable?: true
}

export interface AssistantQueryableEntity extends AssistantQueryableEntitySpec {
  /** Label shown to the model (JSON Schema enum) and used in tool errors. */
  label: string
  /** Singular noun for compact result messages (`1 pedido`). */
  singularLabel: string
}

/**
 * Full catalog of module-backed entities (manual fields only). Filtered per
 * user via `filterQueryableEntities` — schema size tracks what the user can open.
 */
export const ASSISTANT_QUERYABLE_ENTITIES: readonly AssistantQueryableEntitySpec[] = [
  { entity: 'Brand', pluralLabel: 'marcas', moduleSlug: 'brand', creatable: true },
  { entity: 'Color', pluralLabel: 'cores', moduleSlug: 'color', creatable: true },
  { entity: 'Component', pluralLabel: 'componentes', moduleSlug: 'component' },
  { entity: 'CostCenter', pluralLabel: 'centros de custo', moduleSlug: 'costcenter', creatable: true },
  {
    entity: 'DistributionCenter',
    pluralLabel: 'centros de distribuição',
    moduleSlug: 'distributioncenter',
  },
  { entity: 'Factory', pluralLabel: 'fábricas', moduleSlug: 'factory' },
  { entity: 'HandlingUnit', pluralLabel: 'volumes', moduleSlug: 'handlingunit' },
  { entity: 'Invoice', pluralLabel: 'notas fiscais', moduleSlug: 'invoice' },
  { entity: 'Lot', pluralLabel: 'lotes', moduleSlug: 'lot' },
  { entity: 'Machine', pluralLabel: 'máquinas', moduleSlug: 'machine' },
  {
    entity: 'MachineGroup',
    pluralLabel: 'grupos de máquinas',
    moduleSlug: 'machinegroup',
    creatable: true,
  },
  { entity: 'Operation', pluralLabel: 'operações', moduleSlug: 'operation' },
  { entity: 'Party', pluralLabel: 'parceiros', moduleSlug: 'party' },
  { entity: 'Process', pluralLabel: 'processos', moduleSlug: 'process' },
  { entity: 'Product', pluralLabel: 'produtos', moduleSlug: 'product' },
  {
    entity: 'ProductGroup',
    pluralLabel: 'grupos de produto',
    moduleSlug: 'productgroup',
    creatable: true,
  },
  { entity: 'ProductUnit', pluralLabel: 'unidades do produto', moduleSlug: 'productunit' },
  {
    entity: 'ProductionEntry',
    pluralLabel: 'apontamentos de produção',
    moduleSlug: 'productionentry',
  },
  {
    entity: 'ProductionOrder',
    pluralLabel: 'ordens de produção',
    moduleSlug: 'productionorder',
  },
  {
    entity: 'ProductionRun',
    pluralLabel: 'execuções de produção',
    moduleSlug: 'productionrun',
  },
  {
    entity: 'ProductionStop',
    pluralLabel: 'paradas de produção',
    moduleSlug: 'productionstop',
  },
  {
    entity: 'ProductionStopReason',
    pluralLabel: 'motivos de parada',
    moduleSlug: 'productionstopreason',
    creatable: true,
  },
  {
    entity: 'ProductionTicket',
    pluralLabel: 'talões de produção',
    moduleSlug: 'productionticket',
  },
  { entity: 'PurchaseOrder', pluralLabel: 'ordens de compra', moduleSlug: 'purchaseorder' },
  { entity: 'SalesOrder', pluralLabel: 'pedidos', moduleSlug: 'salesorder' },
  { entity: 'SavedSelection', pluralLabel: 'seleções salvas', moduleSlug: 'savedselection' },
  { entity: 'Sector', pluralLabel: 'setores', moduleSlug: 'sector', creatable: true },
  { entity: 'SizeGrid', pluralLabel: 'grades de tamanhos', moduleSlug: 'sizegrid' },
  {
    entity: 'StandardPackaging',
    pluralLabel: 'embalagens padrão',
    moduleSlug: 'standardpackaging',
  },
  { entity: 'StockLocation', pluralLabel: 'locais de estoque', moduleSlug: 'stocklocation' },
  {
    entity: 'StorageAddress',
    pluralLabel: 'endereços de armazenamento',
    moduleSlug: 'storageaddress',
  },
  {
    entity: 'StorageAddressPattern',
    pluralLabel: 'padrões de endereço',
    moduleSlug: 'storageaddresspattern',
  },
  { entity: 'TechnicalSheet', pluralLabel: 'fichas técnicas', moduleSlug: 'technicalsheet' },
  {
    entity: 'TechnicalSheetOverride',
    pluralLabel: 'overrides de ficha técnica',
    moduleSlug: 'technicalsheetoverride',
  },
  {
    entity: 'TicketWorkspace',
    pluralLabel: 'espaços de tickets',
    moduleSlug: 'ticketworkspaces',
  },
  { entity: 'Unit', pluralLabel: 'unidades de medida', moduleSlug: 'unit', creatable: true },
  {
    entity: 'VariationOption',
    pluralLabel: 'opções de variação',
    moduleSlug: 'variationoption',
  },
  { entity: 'WarehouseMap', pluralLabel: 'de-paras de depósito', moduleSlug: 'warehousemap' },
  { entity: 'WarehouseTask', pluralLabel: 'tarefas de armazém', moduleSlug: 'warehousetask' },
  {
    entity: 'WarehouseTaskType',
    pluralLabel: 'tipos de tarefa de armazém',
    moduleSlug: 'warehousetasktype',
  },
  { entity: 'WorkCalendar', pluralLabel: 'calendários de trabalho', moduleSlug: 'workcalendar' },
]

/**
 * Resolve singular/plural labels from the registry. Missing registration →
 * `undefined` (out of the tool enum); `getDescriptorForRule` already warns via
 * `rtLogService` and does not throw outside strict mode.
 */
export function resolveQueryableEntity(
  spec: AssistantQueryableEntitySpec,
): AssistantQueryableEntity | undefined {
  const descriptor = getDescriptorForRule(spec.entity, 'assistant-queryable')
  if (!descriptor) return undefined
  return {
    entity: spec.entity,
    label: descriptor.label,
    singularLabel: descriptor.label.toLocaleLowerCase('pt-BR'),
    pluralLabel: spec.pluralLabel,
    moduleSlug: spec.moduleSlug,
    ...(spec.creatable ? { creatable: true as const } : {}),
  }
}

/** Entities the user can reach given their available module instances. */
export function filterQueryableEntities(
  instances: readonly { slug: string }[],
): AssistantQueryableEntity[] {
  const slugs = new Set(instances.map((i) => i.slug))
  const out: AssistantQueryableEntity[] = []
  for (const spec of ASSISTANT_QUERYABLE_ENTITIES) {
    if (!slugs.has(spec.moduleSlug)) continue
    const resolved = resolveQueryableEntity(spec)
    if (resolved) out.push(resolved)
  }
  return out
}

/** Creatable subset the user can reach (same module-access gate as queryable). */
export function filterCreatableEntities(
  instances: readonly { slug: string }[],
): AssistantQueryableEntity[] {
  return filterQueryableEntities(instances).filter((e) => e.creatable === true)
}

/** Stable fingerprint of the queryable subset (labels, sorted). */
export function queryableCatalogKey(instances: readonly { slug: string }[]): string {
  return filterQueryableEntities(instances)
    .map((e) => e.label)
    .sort()
    .join('\n')
}
